import React, { useState, useEffect } from 'react';
import { ExternalLink, Play, Globe } from 'lucide-react';
import { isYouTubeUrl, isVideoUrl, getDomainFromUrl } from '@/lib/utils';

interface LinkMetadata {
  title?: string;
  description?: string;
  image?: string;
  url: string;
  siteName?: string;
}

interface LinkPreviewProps {
  url: string;
}

export const LinkPreview: React.FC<LinkPreviewProps> = ({ url }) => {
  const [metadata, setMetadata] = useState<LinkMetadata | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchMetadata = async () => {
      setIsLoading(true);
      setError(null);

      try {
        // For YouTube URLs, try to fetch Open Graph metadata first
        if (isYouTubeUrl(url)) {
          const videoId = extractYouTubeVideoId(url);
          if (videoId) {
            try {
              // Try to fetch Open Graph metadata from the YouTube page
              const response = await fetch(`/api/community/link-preview?url=${encodeURIComponent(url)}`);
              if (response.ok) {
                const data = await response.json();
                if (data.success && data.data.title) {
                  setMetadata(data.data);
                  setIsLoading(false);
                  return;
                }
              }
            } catch (error) {
              console.log('Failed to fetch YouTube metadata, using fallback');
            }
            
            // Fallback to basic YouTube preview
            const youtubeMetadata: LinkMetadata = {
              title: 'YouTube Video',
              description: 'Click to watch this YouTube video',
              image: `https://img.youtube.com/vi/${videoId}/maxresdefault.jpg`,
              url,
              siteName: 'YouTube'
            };
            setMetadata(youtubeMetadata);
            setIsLoading(false);
            return;
          }
        }

        // For other URLs, try to fetch Open Graph metadata
        const response = await fetch(`/api/link-preview?url=${encodeURIComponent(url)}`);
        
        if (!response.ok) {
          throw new Error('Failed to fetch link preview');
        }

        const data = await response.json();
        setMetadata(data);
      } catch (err) {
        console.error('Error fetching link preview:', err);
        setError('Failed to load preview');
        
        // Fallback: create basic metadata from URL
        const fallbackMetadata: LinkMetadata = {
          title: getDomainFromUrl(url),
          description: url,
          url,
          siteName: getDomainFromUrl(url)
        };
        setMetadata(fallbackMetadata);
      } finally {
        setIsLoading(false);
      }
    };

    fetchMetadata();
  }, [url]);

  const extractYouTubeVideoId = (url: string): string | null => {
    const patterns = [
      /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([^&\n?#]+)/,
      /youtube\.com\/v\/([^&\n?#]+)/,
      /youtube\.com\/watch\?.*v=([^&\n?#]+)/
    ];

    for (const pattern of patterns) {
      const match = url.match(pattern);
      if (match) {
        return match[1];
      }
    }
    return null;
  };

  if (isLoading) {
    return (
      <div className="border border-gray-200 rounded-lg p-4 animate-pulse">
        <div className="flex flex-col sm:flex-row space-y-3 sm:space-y-0 sm:space-x-3">
          <div className="bg-gray-200 h-32 sm:h-16 w-full sm:w-24 rounded"></div>
          <div className="flex-1 space-y-2">
            <div className="bg-gray-200 h-4 w-3/4 rounded"></div>
            <div className="bg-gray-200 h-3 w-1/2 rounded"></div>
            <div className="bg-gray-200 h-3 w-1/4 rounded"></div>
          </div>
        </div>
      </div>
    );
  }

  if (error || !metadata) {
    return (
      <div className="border border-gray-200 rounded-lg p-3">
        <a
          href={url}
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center space-x-2 text-blue-600 hover:text-blue-700"
        >
          <Globe className="h-4 w-4" />
          <span className="text-sm">{url}</span>
          <ExternalLink className="h-3 w-3" />
        </a>
      </div>
    );
  }

  return (
    <div className="border border-gray-200 rounded-lg overflow-hidden hover:border-gray-300 transition-colors">
      <a
        href={url}
        target="_blank"
        rel="noopener noreferrer"
        className="block"
      >
        <div className="flex flex-col sm:flex-row">
          {/* Thumbnail */}
          {metadata.image && (
            <div className="flex-shrink-0 w-full sm:w-24 h-32 sm:h-16 bg-gray-100 relative">
              <img
                src={metadata.image}
                alt={metadata.title || 'Link preview'}
                className="w-full h-full object-cover"
                onError={(e) => {
                  // Hide image on error
                  e.currentTarget.style.display = 'none';
                }}
              />
              {isVideoUrl(url) && (
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="bg-black bg-opacity-50 rounded-full p-1">
                    <Play className="h-4 w-4 text-white fill-current" />
                  </div>
                </div>
              )}
            </div>
          )}
          
          {/* Content */}
          <div className="flex-1 p-3 min-w-0">
            <div className="space-y-1">
              <h4 className="text-sm font-medium text-gray-900 line-clamp-2">
                {metadata.title || metadata.siteName || getDomainFromUrl(url)}
              </h4>
              {metadata.description && (
                <p className="text-xs text-gray-600 line-clamp-2">
                  {metadata.description}
                </p>
              )}
              <div className="flex items-center space-x-1 text-xs text-gray-500">
                <Globe className="h-3 w-3" />
                <span className="truncate">{metadata.siteName || getDomainFromUrl(url)}</span>
                <ExternalLink className="h-3 w-3 flex-shrink-0" />
              </div>
            </div>
          </div>
        </div>
      </a>
    </div>
  );
}; 